import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { generateUuid } from '../../../../../../../../../../utils/uuids'
import { WbsItemInfo, WbsItemLink } from '../types'

// Dependency represents a link between two tasks
export interface Dependency extends WbsItemLink {
  fromEndDate?: Date | null // fromRow's scheduledEnd
  toStartDate?: Date | null // toRow's scheduledStart
}

// Props for DependencyProvider
export interface DependencyProviderProps {
  children: React.ReactNode
  // Initial dependencies (from parent/API)
  dependencies?: Dependency[]
  // Callback when dependencies change (for parent to handle persistence)
  onDependenciesChange?: (dependencies: Dependency[]) => void
  // WBS item info map for resolving display names/icons of hidden items
  wbsItemInfoMap?: ReadonlyMap<string, WbsItemInfo>
}

// WBS item type category for dependency compatibility check
export type DependencyItemType = 'task' | 'deliverable'

// Drag state during D&D operation
export interface DragState {
  isDragging: boolean
  // Source information
  fromRowIndex: number | null
  fromSide: 'start' | 'end' | null // 'start' = left circle (scheduledStart), 'end' = right circle (scheduledEnd)
  fromItemType: DependencyItemType | null // Source WBS item type
  // Start position in pixels (relative to gantt chart container)
  startX: number
  startY: number
  // Current mouse position
  currentX: number
  currentY: number
}

// Row date information for dependency line drawing
export interface RowDateInfo {
  scheduledStart?: Date | null
  scheduledEnd?: Date | null
  wbsItemUuid?: string
  projectPlanUuid?: string
}

// ============================================================================
// Drag Context (separated to prevent re-renders during D&D)
// ============================================================================

export interface DragContextValue {
  dragState: DragState
  startDrag: (params: {
    fromRowIndex: number
    fromSide: 'start' | 'end'
    fromItemType: DependencyItemType
    startX: number
    startY: number
  }) => void
  updateDragPosition: (x: number, y: number) => void
  endDrag: () => void
}

const defaultDragState: DragState = {
  isDragging: false,
  fromRowIndex: null,
  fromSide: null,
  fromItemType: null,
  startX: 0,
  startY: 0,
  currentX: 0,
  currentY: 0,
}

const DragContext = createContext<DragContextValue | null>(null)

export const DragProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [dragState, setDragState] = useState<DragState>(defaultDragState)

  const startDrag = useCallback(
    (params: {
      fromRowIndex: number
      fromSide: 'start' | 'end'
      fromItemType: DependencyItemType
      startX: number
      startY: number
    }) => {
      setDragState({
        isDragging: true,
        fromRowIndex: params.fromRowIndex,
        fromSide: params.fromSide,
        fromItemType: params.fromItemType,
        startX: params.startX,
        startY: params.startY,
        currentX: params.startX,
        currentY: params.startY,
      })
    },
    []
  )

  const updateDragPosition = useCallback((x: number, y: number) => {
    setDragState(prev => ({
      ...prev,
      currentX: x,
      currentY: y,
    }))
  }, [])

  const endDrag = useCallback(() => {
    setDragState(defaultDragState)
  }, [])

  const contextValue = useMemo(
    () => ({
      dragState,
      startDrag,
      updateDragPosition,
      endDrag,
    }),
    [dragState, startDrag, updateDragPosition, endDrag]
  )

  return (
    <DragContext.Provider value={contextValue}>{children}</DragContext.Provider>
  )
}

export const useDependencyDragContextOptional = (): DragContextValue | null => {
  return useContext(DragContext)
}

// ============================================================================
// Dependency Context (static data - does not change during D&D)
// ============================================================================

// Input type for adding a new dependency (uuid and wbsItemUuids are auto-resolved)
export type AddDependencyInput = Omit<
  Dependency,
  | 'uuid'
  | 'fromWbsItemUuid'
  | 'toWbsItemUuid'
  | 'fromProjectPlanUuid'
  | 'toProjectPlanUuid'
  | 'fromEndDate'
  | 'toStartDate'
> &
  Partial<
    Pick<
      Dependency,
      | 'uuid'
      | 'fromWbsItemUuid'
      | 'toWbsItemUuid'
      | 'fromProjectPlanUuid'
      | 'toProjectPlanUuid'
    >
  >

// Updates for color/description of a dependency link
export type DependencyLinkUpdates = {
  color?: string
  description?: string
}

interface DependencyContextValue {
  // Dependencies data
  dependencies: Dependency[]
  addDependency: (dep: AddDependencyInput) => void
  removeDependency: (fromRowIndex: number, toRowIndex: number) => void
  removeDependencyByUuid: (uuid: string) => void
  updateDependencyLink: (uuid: string, updates: DependencyLinkUpdates) => void

  // Row date tracking (updates dependency dates internally)
  updateRowDates: (rowIndex: number, dates: RowDateInfo) => void

  // wbsItemUuid → rowIndex mapping for cascade slide
  getWbsItemUuidToRowIndexMap: () => ReadonlyMap<string, number>

  // WBS item info for resolving display names/icons of hidden items
  wbsItemInfoMap: ReadonlyMap<string, WbsItemInfo>
}

const DependencyContext = createContext<DependencyContextValue | null>(null)

const EMPTY_WBS_ITEM_INFO_MAP: ReadonlyMap<string, WbsItemInfo> = new Map()

export const DependencyProvider: React.FC<DependencyProviderProps> = ({
  children,
  dependencies: dependenciesProp,
  onDependenciesChange,
  wbsItemInfoMap,
}) => {
  const [dependencyState, setDependencyState] = useState<Dependency[]>(
    dependenciesProp ?? []
  )

  // Internal ref for date lookups (not exposed to context consumers)
  const rowDatesRef = useRef<Map<number, RowDateInfo>>(new Map())
  // Track wbsItemUuid → rowIndex to detect row index changes after reorder
  const wbsItemUuidToRowIndexRef = useRef<Map<string, number>>(new Map())

  const updateRowDates = useCallback((rowIndex: number, dates: RowDateInfo) => {
    // Track UUID → rowIndex mapping and detect row movement
    let rowIndexChanged = false
    if (dates.wbsItemUuid) {
      const prevRowIndex = wbsItemUuidToRowIndexRef.current.get(
        dates.wbsItemUuid
      )
      wbsItemUuidToRowIndexRef.current.set(dates.wbsItemUuid, rowIndex)
      rowIndexChanged = prevRowIndex !== undefined && prevRowIndex !== rowIndex

      // Clean up stale rowDatesRef entry from previous position
      if (rowIndexChanged) {
        const oldEntry = rowDatesRef.current.get(prevRowIndex!)
        if (oldEntry?.wbsItemUuid === dates.wbsItemUuid) {
          rowDatesRef.current.delete(prevRowIndex!)
        }
      }
    }

    const existing = rowDatesRef.current.get(rowIndex)
    const startChanged =
      existing?.scheduledStart?.getTime() !== dates.scheduledStart?.getTime()
    const endChanged =
      existing?.scheduledEnd?.getTime() !== dates.scheduledEnd?.getTime()
    const uuidChanged = existing?.wbsItemUuid !== dates.wbsItemUuid

    if (!startChanged && !endChanged && !uuidChanged && !rowIndexChanged) return

    rowDatesRef.current.set(rowIndex, dates)

    // Update dependencies: row indices (if moved) and embedded dates
    setDependencyState(prev => {
      let changed = false
      const next = prev.map(dep => {
        let newDep = dep

        // fromRowIndexを更新（行移動時、または折りたたみ状態でfetch後の初回登録時）
        if (
          dates.wbsItemUuid &&
          dep.fromWbsItemUuid === dates.wbsItemUuid &&
          dep.fromRowIndex !== rowIndex
        ) {
          newDep = { ...newDep, fromRowIndex: rowIndex }
          changed = true
        }
        // toRowIndexを更新（同上）
        if (
          dates.wbsItemUuid &&
          dep.toWbsItemUuid === dates.wbsItemUuid &&
          dep.toRowIndex !== rowIndex
        ) {
          newDep = { ...newDep, toRowIndex: rowIndex }
          changed = true
        }

        // Update embedded dates — match by wbsItemUuid (not rowIndex) to avoid
        // incorrectly overwriting hidden items' dates when another item shifts
        // into the same rowIndex after collapse.
        if (dates.wbsItemUuid && newDep.fromWbsItemUuid === dates.wbsItemUuid) {
          const newEnd = dates.scheduledEnd ?? null
          if (newDep.fromEndDate?.getTime() !== newEnd?.getTime()) {
            newDep = { ...newDep, fromEndDate: newEnd }
            changed = true
          }
        }
        if (dates.wbsItemUuid && newDep.toWbsItemUuid === dates.wbsItemUuid) {
          const newStart = dates.scheduledStart ?? null
          if (newDep.toStartDate?.getTime() !== newStart?.getTime()) {
            newDep = { ...newDep, toStartDate: newStart }
            changed = true
          }
        }

        return newDep
      })
      return changed ? next : prev
    })
  }, [])

  // Sync with dependencies prop when it changes from parent
  // Resolve dates from rowDatesRef since the prop may not include them
  useEffect(() => {
    if (dependenciesProp !== undefined) {
      const resolved = dependenciesProp.map(dep => {
        const fromDates = rowDatesRef.current.get(dep.fromRowIndex)
        const toDates = rowDatesRef.current.get(dep.toRowIndex)
        return {
          ...dep,
          fromEndDate: dep.fromEndDate ?? fromDates?.scheduledEnd ?? null,
          toStartDate: dep.toStartDate ?? toDates?.scheduledStart ?? null,
        }
      })
      setDependencyState(resolved)
    }
  }, [dependenciesProp])

  // Notify parent when dependencies change
  useEffect(() => {
    if (onDependenciesChange) {
      onDependenciesChange(dependencyState)
    }
  }, [dependencyState, onDependenciesChange])

  const addDependency = useCallback((dep: AddDependencyInput) => {
    setDependencyState(prev => {
      // Check if dependency already exists
      const exists = prev.some(
        d =>
          d.fromRowIndex === dep.fromRowIndex && d.toRowIndex === dep.toRowIndex
      )
      if (exists) {
        return prev
      }
      // Resolve dates, wbsItemUuids, and projectPlanUuids from ref
      const fromDates = rowDatesRef.current.get(dep.fromRowIndex)
      const toDates = rowDatesRef.current.get(dep.toRowIndex)
      return [
        ...prev,
        {
          ...dep,
          uuid: dep.uuid || generateUuid(),
          fromWbsItemUuid: dep.fromWbsItemUuid || fromDates?.wbsItemUuid || '',
          toWbsItemUuid: dep.toWbsItemUuid || toDates?.wbsItemUuid || '',
          fromProjectPlanUuid:
            dep.fromProjectPlanUuid || fromDates?.projectPlanUuid || '',
          toProjectPlanUuid:
            dep.toProjectPlanUuid || toDates?.projectPlanUuid || '',
          fromEndDate: fromDates?.scheduledEnd ?? null,
          toStartDate: toDates?.scheduledStart ?? null,
        },
      ]
    })
  }, [])

  const getWbsItemUuidToRowIndexMap = useCallback(
    (): ReadonlyMap<string, number> => wbsItemUuidToRowIndexRef.current,
    []
  )

  const removeDependency = useCallback(
    (fromRowIndex: number, toRowIndex: number) => {
      setDependencyState(prev =>
        prev.filter(
          d => !(d.fromRowIndex === fromRowIndex && d.toRowIndex === toRowIndex)
        )
      )
    },
    []
  )

  const removeDependencyByUuid = useCallback((uuid: string) => {
    setDependencyState(prev => prev.filter(d => d.uuid !== uuid))
  }, [])

  const updateDependencyLink = useCallback(
    (uuid: string, updates: DependencyLinkUpdates) => {
      setDependencyState(prev =>
        prev.map(d => (d.uuid === uuid ? { ...d, ...updates } : d))
      )
    },
    []
  )

  const contextValue = useMemo(
    () => ({
      dependencies: dependencyState,
      addDependency,
      removeDependency,
      removeDependencyByUuid,
      updateDependencyLink,
      updateRowDates,
      getWbsItemUuidToRowIndexMap,
      wbsItemInfoMap: wbsItemInfoMap ?? EMPTY_WBS_ITEM_INFO_MAP,
    }),
    [
      dependencyState,
      addDependency,
      removeDependency,
      removeDependencyByUuid,
      updateDependencyLink,
      updateRowDates,
      getWbsItemUuidToRowIndexMap,
      wbsItemInfoMap,
    ]
  )

  return (
    <DragProvider>
      <DependencyContext.Provider value={contextValue}>
        {children}
      </DependencyContext.Provider>
    </DragProvider>
  )
}

export const useDependencyContext = (): DependencyContextValue | null => {
  return useContext(DependencyContext)
}
